import { InjectionToken, Signal } from '@angular/core';
import { ActivityEntityName } from '@jms/model';
import type { EntityOperationPresentation, EntitySurfaceOperation } from '@workbuddy/services/entities';
// Deep path, not the barrel: the inspector barrel pulls in its host and service, and this file is imported
// by the surface engine — the barrel would close a cycle. The inspector's contracts import nothing back.
import type { CanDeactivateInspector, EntityInspectorDeactivateReason } from '@workbuddy/ui/v2/blocks/entity.inspector/entity.inspector.contracts';

/** Named router-outlet for shell-hosted dialogs. Siblings of the `inspector` outlet, above it in paint order. */
export const ENTITY_DIALOG_OUTLET = 'dialog';

/** History-state marker for an invocation whose initial values and callbacks remain in memory. */
export const ENTITY_DIALOG_STATE_ID = '__wbDialogId';

export type EntityDialogOperation = Extract<EntitySurfaceOperation, 'create' | 'edit'> | 'expand';

interface EntityDialogRequestBase {
  entity: ActivityEntityName;
  /**
   * Opaque host bag (e.g. board `params`). Surface operations receive it through their mount context;
   * custom operation components receive it through {@link ENTITY_DIALOG_SESSION}. The host never
   * interprets its keys.
   */
  params?: unknown;
  /** Where this was opened from, so a save can refresh the originating board. */
  source?: EntityDialogSourceRef;
  /** Runs after a committed save and source invalidation, before the dialog closes. */
  afterSave?: (result: unknown) => void;
  /**
   * Optional panel sizing override. When omitted, sizing comes from the operation construction
   * (`presentation` on `operations.create` / edit): `dialog` mode sizes the panel; `pseudo-page`
   * (or surface default) fills the working area.
   */
  presentation?: EntityDialogPresentation;
}

/** Sized-panel chrome. `undefined` on the host means pseudo-page (fill the working area). */
export interface EntityDialogPresentation {
  title?: string;
  width?: string;
  /** Omit to hug content. Set for a fixed box (`min(860px, calc(100vh - 48px))`, `auto`, …). */
  height?: string;
  /** Stored for callers; the host ignores this and caps to the padded working area. */
  maxHeight?: string;
  /** Vertical placement of a sized panel. Default `top`. */
  align?: 'top' | 'center';
  /** `none` withholds the host create header for a body that draws its own. Defaults to `host`. */
  chrome?: 'host' | 'none';
}

/**
 * Maps manifest {@link EntityOperationPresentation} to host panel sizing.
 * `undefined` / `pseudo-page` → stretch panel; `dialog` → sized panel (hug unless `height` is set).
 */
export function toEntityDialogPresentation(presentation?: EntityOperationPresentation): EntityDialogPresentation | undefined {

  if (!presentation || presentation.mode === 'pseudo-page') return undefined;
  return { title: presentation.title, width: presentation.width, height: presentation.height, maxHeight: presentation.maxHeight, align: presentation.align, chrome: presentation.chrome };
}

/** Host create header is only for sized dialogs that did not opt out. A pseudo-page already has surface chrome. */
export function entityDialogShowsCreateHeader(operation: EntityDialogOperation, presentation: EntityDialogPresentation | undefined): boolean {

  return operation === 'create' && !!presentation && presentation.chrome !== 'none';
}

/**
 * What a caller passes to {@link EntityDialogs.open}.
 *
 * Create has no id and may carry `initialValues` and/or host `params`; edit requires an id and may
 * carry host `params`, but never field seeds. Distinct shapes make invalid requests a compile error before the route-level
 * check handles pasted URLs.
 *
 * Invocation-local data (`params`, `initialValues`, `afterSave`, `source`) is staged in memory and
 * claimed by the host; it is not reconstructible after a full page reload.
 */
export type EntityDialogRequest =
  | EntityDialogRequestBase & { operation: 'create'; id?: never; initialValues?: Record<string, unknown> }
  | EntityDialogRequestBase & { operation: 'edit' | 'expand'; id: string; initialValues?: never };

/**
 * Session injected into custom operation components (`operations.*.kind: 'custom'`).
 * Surfaces use the inspector renderer path and receive params through `EntitySurfaceMountContext`.
 *
 * Inject with {@link ENTITY_DIALOG_SESSION}. Call `notifySaved` after a successful write so the host
 * runs `afterSave`, invalidates `source`, and closes; call `close` for Cancel.
 */
export interface EntityDialogSession {
  /** Opaque host bag from the open request (e.g. board params with live getters). */
  readonly params?: unknown;
  /** Create-only seed from the open request (e.g. `{ pending: true }` from Add Pending). */
  readonly initialValues?: Record<string, unknown>;
  /** Committed save: host afterSave + source invalidation + close. */
  notifySaved(result?: unknown): void;
  /** Guarded dismiss (Cancel / escape path the host already owns). */
  close(): Promise<boolean>;
}

/** Injection token for {@link EntityDialogSession} inside custom operation components. */
export const ENTITY_DIALOG_SESSION = new InjectionToken<EntityDialogSession>('ENTITY_DIALOG_SESSION');

/** Where a dialog was opened from. Drives post-save refresh. */
export interface EntityDialogSourceRef {
  entity: ActivityEntityName;
  id?: string;
  relation?: string;
}

/** Resolved request plus the identity the host mounts against. */
export type EntityDialogInternal = EntityDialogRequest & { dialogId: string };

/**
 * Narrowed from the inspector's union rather than redeclared, because a dialog mounts the **same**
 * renderer and therefore receives the same guard instance. A parallel union would need an adapter at that
 * boundary, and the inspector's contract is explicit that there is no second guard shape.
 *
 * Cancel reports `'close'`: the container is going away with the record unsaved, which is the question
 * `'close'` already asks. `'change-inspector'` cannot occur here — a dialog is never replaced in place,
 * because `open()` refuses to replace a live one without first running this check.
 */
export type EntityDialogDeactivateReason = Extract<EntityInspectorDeactivateReason, 'navigate' | 'close'>;

/**
 * What a dialog's mounted surface implements.
 *
 * `true` means saved, empty, or explicitly discarded. `false` means stay — a navigation is cancelled with
 * the URL intact, and a close is aborted before teardown.
 */
export type CanDeactivateEntityDialog = CanDeactivateInspector;

/**
 * The open/close API a board or surface calls. Abstract class rather than an interface so it is its own
 * injection token.
 */
export abstract class EntityDialogs {
  /** Resolves false when the dialog was refused — denied authorization, or a leave check said stay. */
  public abstract open(request: EntityDialogRequest): Promise<boolean>;
  /** Runs the leave check. Resolves false when it refused. */
  public abstract close(): Promise<boolean>;
  /** True while a dialog is mounted, so callers can avoid stacking a second one. */
  public abstract readonly $active: Signal<boolean>;
}

/**
 * What the route guard injects.
 *
 * Separate from `EntityDialogs` because the guard resolves against the **route** injector and runs
 * before the host component exists — the same constraint that made the inspector split this token out.
 */
export abstract class EntityDialogDeactivateSource {
  public abstract canDeactivateActive(reason: EntityDialogDeactivateReason): boolean | Promise<boolean>;
  /** True while an intentional teardown is in flight, so the route guard does not re-ask. */
  public abstract readonly $deactivating: Signal<boolean>;
  /** True while a leave prompt is already open, so a second trigger refuses instead of prompting twice. */
  public abstract readonly $deactivatePending: Signal<boolean>;
}

/** The read side the host binds to. */
export abstract class EntityDialogSource {
  public abstract claimPending(match: { entity: ActivityEntityName; operation: EntityDialogOperation; id?: string; dialogId?: string }): EntityDialogInternal | null;
  public abstract registerGuard(guard: CanDeactivateEntityDialog | null): void;
}
